import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Grid, Column, Tile, ProgressIndicator, ProgressStep, InlineLoading, Modal, TextInput, InlineNotification } from '@carbon/react';
import { CheckmarkFilled, Printer, Email, Home, Package, WarningAltFilled, Checkmark } from '@carbon/icons-react';
import { useTheme } from '../context/ThemeContext';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { verifyCheckoutSession } from '../services/stripeService';
import { orderService } from '../services/orderService';

const PaymentConfirmationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { theme } = useTheme();
  const { clearCart } = useCart();
  const { user } = useAuth();
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState(null);
  const printRef = useRef(null);
  const cartCleared = useRef(false);
  const orderCreated = useRef(false);
  const purchaseConversionFired = useRef(false);

  const isDark = theme === 'dark';
  const bgPrimary = isDark ? '#161616' : '#ffffff';
  const bgSecondary = isDark ? '#262626' : '#f4f4f4';
  const textPrimary = isDark ? '#f4f4f4' : '#161616';
  const textSecondary = isDark ? '#c6c6c6' : '#525252';
  const borderColor = isDark ? '#393939' : '#e0e0e0';

  useEffect(() => {
    const verifyAndLoadOrder = async () => {
      try {
        // Check for session_id in URL (from Stripe redirect)
        // This page is PUBLIC - no authentication required
        const sessionId = searchParams.get('session_id');
        
        if (!sessionId) {
          // No session_id in URL - check localStorage as fallback
    const saved = localStorage.getItem('chillverify-order');
    if (saved) {
      setOrderDetails(JSON.parse(saved));
            setLoading(false);
            return;
          } else {
            throw new Error('No order session found. Please check your email for order confirmation.');
          }
        }

        console.log('🔍 Verifying checkout session (public):', sessionId);
        setLoading(true);
        
        // Verify the checkout session server-side (no auth required)
        const sessionData = await verifyCheckoutSession(sessionId);
        console.log('✅ Session verified:', sessionData);
        
        // Parse metadata to get order number and additional info
        const metadata = sessionData.metadata || {};
        
        // Use shipping address from session (preferred) or fallback to metadata
        let shippingAddress = sessionData.shippingAddress;
        
        if (!shippingAddress) {
          // Fallback: try to parse from metadata
          try {
            const shippingInfo = metadata.shippingInfo ? JSON.parse(metadata.shippingInfo) : null;
            if (shippingInfo) {
              shippingAddress = shippingInfo.address || {
                firstName: shippingInfo.firstName || '',
                lastName: shippingInfo.lastName || '',
                address: shippingInfo.address?.line1 || shippingInfo.address || '',
                city: shippingInfo.address?.city || shippingInfo.city || '',
                state: shippingInfo.address?.state || shippingInfo.state || '',
                zipCode: shippingInfo.address?.postal_code || shippingInfo.zipCode || '',
                country: shippingInfo.address?.country || shippingInfo.country || 'US',
              };
            }
          } catch (parseError) {
            console.warn('Failed to parse shipping info from metadata:', parseError);
          }
        }
        
        // Calculate shipping cost (total - subtotal - tax)
        const subtotal = sessionData.amountSubtotal || (sessionData.amountTotal * 0.92);
        const tax = sessionData.amountTax || (sessionData.amountTotal * 0.08);
        const shipping = sessionData.amountTotal - subtotal - tax;
        
        // Parse shipping info from metadata for order creation
        let shippingInfo = null;
        try {
          shippingInfo = metadata.shippingInfo ? JSON.parse(metadata.shippingInfo) : null;
        } catch (parseError) {
          console.warn('Failed to parse shipping info from metadata:', parseError);
        }

        // If shippingInfo is not available, construct it from shippingAddress
        if (!shippingInfo && shippingAddress) {
          shippingInfo = {
            firstName: shippingAddress.firstName || '',
            lastName: shippingAddress.lastName || '',
            email: sessionData.customerEmail || '',
            phone: '',
            address: shippingAddress.address || '',
            city: shippingAddress.city || '',
            state: shippingAddress.state || '',
            zipCode: shippingAddress.zipCode || '',
            country: shippingAddress.country || 'US',
          };
        }

        // Create order in Supabase (matching reference app pattern)
        if (!orderCreated.current) {
          try {
            console.log('📦 Creating order in Supabase...');
            const order = await orderService.createOrder({
              status: 'pending',
              total: sessionData.amountTotal,
              items: sessionData.lineItems || [],
              shippingInfo: shippingInfo,
              paymentIntentId: sessionData.paymentIntentId,
              userId: user?.id || metadata.userId !== 'guest' ? metadata.userId : null,
              orderNumber: metadata.orderNumber, // Use order number from metadata if available
            });
            console.log('✅ Order created in Supabase:', order.id, 'Order #:', order.orderNumber);
            orderCreated.current = true;
          } catch (orderError) {
            console.error('❌ Error creating order in Supabase:', orderError);
            // Don't fail the page if order creation fails - webhook will create it
            // But log the error for debugging
          }
        }

        // Format order details from session for display
        const orderData = {
          orderNumber: metadata.orderNumber || sessionData.orderId || sessionId.substring(0, 20).toUpperCase(),
          total: sessionData.amountTotal,
          subtotal: subtotal,
          tax: tax,
          shipping: shipping > 0 ? shipping : 0,
          shippingAddress: shippingAddress,
          paymentMethod: '**** **** **** ' + (sessionData.paymentIntentId?.slice(-4) || '****'),
          orderDate: new Date().toISOString(),
          items: sessionData.lineItems || [], // Use line items from verified session
          email: sessionData.customerEmail,
        };
        setOrderDetails(orderData);
        // Set email address for modal if available
        if (sessionData.customerEmail) {
          setEmailAddress(sessionData.customerEmail);
        }

        // Google Ads Purchase conversion (fire once per Stripe session)
        const conversionKey = `gtag_purchase_${sessionId}`;
        if (
          !purchaseConversionFired.current &&
          typeof window !== 'undefined' &&
          typeof window.gtag === 'function' &&
          !sessionStorage.getItem(conversionKey)
        ) {
          purchaseConversionFired.current = true;
          sessionStorage.setItem(conversionKey, '1');
          window.gtag('event', 'conversion', {
            send_to: 'AW-18317438055/uIq9CNjd5dEcEOfYt55E',
            transaction_id: sessionId,
            value: typeof sessionData.amountTotal === 'number' ? sessionData.amountTotal : undefined,
            currency: 'AUD',
          });
        }
        
        // Clear cart after successful payment verification
        if (!cartCleared.current) {
          console.log('✅ Payment verified, clearing cart...');
          clearCart();
          cartCleared.current = true;
        }
      } catch (err) {
        console.error('❌ Error processing order:', err);
        setError(err instanceof Error ? err.message : 'Failed to process order. Please contact support if this issue persists.');
      } finally {
        setLoading(false);
      }
    };

    verifyAndLoadOrder();
  }, [searchParams, clearCart, user?.id]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const handlePrint = () => {
    window.print();
  };

  const handleEmailReceipt = async () => {
    if (!emailAddress || !emailAddress.includes('@')) {
      setEmailError('Please enter a valid email address');
      return;
    }

    if (!orderDetails) {
      setEmailError('Order details not available');
      return;
    }

    setEmailSending(true);
    setEmailError(null);

    try {
      const API_BASE_URL = process.env.NODE_ENV === 'production'
        ? '/.netlify/functions'
        : 'http://localhost:8888/.netlify/functions';

      const response = await fetch(`${API_BASE_URL}/send-receipt-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: emailAddress,
          orderNumber: orderDetails.orderNumber,
          orderDate: orderDetails.orderDate,
          items: orderDetails.items || [],
          subtotal: orderDetails.subtotal,
          tax: orderDetails.tax,
          shipping: orderDetails.shipping,
          total: orderDetails.total,
          shippingAddress: orderDetails.shippingAddress,
          paymentMethod: orderDetails.paymentMethod,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to send email' }));
        throw new Error(errorData.error || 'Failed to send email');
      }

      setEmailSent(true);
      setTimeout(() => {
        setIsEmailModalOpen(false);
        setEmailSent(false);
        setEmailAddress(orderDetails.email || '');
      }, 2000);
    } catch (err) {
      console.error('Error sending receipt email:', err);
      setEmailError(err.message || 'Failed to send email. Please try again.');
    } finally {
      setEmailSending(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div style={{ backgroundColor: bgPrimary, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <InlineLoading description="Processing your order..." />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{ backgroundColor: bgPrimary, minHeight: '100vh', padding: '32px' }}>
        <Grid>
          <Column lg={{ span: 10, offset: 3 }} md={8} sm={4}>
            <Tile style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, padding: '32px', textAlign: 'center' }}>
              <div style={{ marginBottom: '16px' }}>
                <WarningAltFilled size={64} style={{ color: '#da1e28' }} />
              </div>
              <h2 style={{ color: textPrimary, marginBottom: '16px' }}>Order Processing Error</h2>
              <p style={{ color: textSecondary, marginBottom: '24px' }}>{error}</p>
              <Button kind="primary" onClick={() => navigate('/cart')}>
                Return to Cart
              </Button>
            </Tile>
          </Column>
        </Grid>
      </div>
    );
  }

  // Default order details if none found
  const order = orderDetails || {
    items: [{ name: 'Sample Product', quantity: 1, price: 99.99, image: '/assets/1-Black-KBeacon.webp' }],
    subtotal: 99.99,
    shipping: 0,
    tax: 8.00,
    total: 107.99,
    shippingAddress: { firstName: 'John', lastName: 'Doe', address: '123 Main St', city: 'San Francisco', state: 'CA', zipCode: '94102' },
    paymentMethod: '**** 3456',
    orderNumber: 'CV-DEMO12345',
    orderDate: new Date().toISOString(),
  };

  return (
    <div style={{ backgroundColor: bgPrimary, minHeight: '100vh' }} data-testid="confirmation-page">
      {/* Progress Header */}
      <div style={{ backgroundColor: bgSecondary, borderBottom: `1px solid ${borderColor}`, padding: '16px 0' }}>
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <div style={{ maxWidth: '600px', margin: '0 auto' }}>
              <ProgressIndicator currentIndex={2} spaceEqually>
                <ProgressStep label="Shipping" />
                <ProgressStep label="Payment" />
                <ProgressStep label="Confirmation" />
              </ProgressIndicator>
            </div>
          </Column>
        </Grid>
      </div>

      {/* Content */}
      <div style={{ padding: '16px 0 32px' }} className="print-content">
        <Grid>
          <Column lg={{ span: 10, offset: 3 }} md={8} sm={4}>
            {/* Success Message */}
            <Tile style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, padding: '32px 20px', textAlign: 'center', marginBottom: '16px' }} className="no-print">
              <div style={{
                backgroundColor: '#24a148',
                borderRadius: '50%',
                width: '64px',
                height: '64px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
              }}>
                <CheckmarkFilled size={36} style={{ color: '#ffffff' }} />
              </div>
              
              <h1 style={{ color: textPrimary, fontSize: '1.5rem', fontWeight: 600, marginBottom: '8px' }}>
                Order Confirmed!
              </h1>
              <p style={{ color: textSecondary, marginBottom: '4px', fontSize: '0.875rem' }}>
                Thank you for your purchase
              </p>
              <p style={{ color: textPrimary, fontSize: '1.125rem', fontWeight: 600, marginBottom: '20px' }}>
                Order #{order.orderNumber}
              </p>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }} className="no-print">
                <Button kind="tertiary" size="sm" renderIcon={Printer} onClick={handlePrint} data-testid="print-receipt">
                  Print Receipt
                </Button>
                <Button kind="tertiary" size="sm" renderIcon={Email} onClick={() => setIsEmailModalOpen(true)} data-testid="email-receipt">
                  Email Receipt
                </Button>
              </div>
            </Tile>

            {/* Order Details */}
            <Tile style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, padding: '20px', marginBottom: '16px' }}>
              <h3 style={{ color: textPrimary, fontSize: '1rem', fontWeight: 600, marginBottom: '16px' }}>Order Details</h3>
              
              {/* Order Info Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '16px', marginBottom: '20px' }}>
                <div>
                  <p style={{ color: textSecondary, fontSize: '0.75rem', marginBottom: '4px' }}>ORDER DATE</p>
                  <p style={{ color: textPrimary, fontSize: '0.875rem', margin: 0 }}>{formatDate(order.orderDate)}</p>
                </div>
                <div>
                  <p style={{ color: textSecondary, fontSize: '0.75rem', marginBottom: '4px' }}>DELIVERY</p>
                  <p style={{ color: textPrimary, fontSize: '0.875rem', margin: 0 }}>3-5 Business Days</p>
                </div>
                <div>
                  <p style={{ color: textSecondary, fontSize: '0.75rem', marginBottom: '4px' }}>SHIPPING</p>
                  <p style={{ color: textPrimary, fontSize: '0.875rem', margin: 0 }}>Standard</p>
                </div>
              </div>

              {/* Items */}
              <p style={{ color: textSecondary, fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', marginBottom: '12px' }}>Items Ordered</p>
              {order.items && order.items.length > 0 ? order.items.map((item, idx) => (
                <div
                  key={item.id || `item-${idx}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '12px 0',
                    borderBottom: idx < order.items.length - 1 ? `1px solid ${borderColor}` : 'none',
                  }}
                >
                  <img
                    src={item.image || '/assets/1-Black-KBeacon.webp'}
                    alt={item.name}
                    style={{
                      width: '50px',
                      height: '50px',
                      objectFit: 'contain',
                      backgroundColor: '#ffffff',
                      borderRadius: '4px',
                      padding: '4px',
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ color: textPrimary, fontWeight: 500, fontSize: '0.875rem', margin: 0, marginBottom: '2px' }}>{item.name}</p>
                    <p style={{ color: textSecondary, fontSize: '0.75rem', margin: 0 }}>Qty: {item.quantity}</p>
                  </div>
                  <p style={{ color: textPrimary, fontWeight: 600, fontSize: '0.875rem', margin: 0 }}>${(item.price * item.quantity).toFixed(2)}</p>
                </div>
              )) : (
                <p style={{ color: textSecondary, fontSize: '0.875rem', fontStyle: 'italic' }}>
                  Order details are being processed. You'll receive a confirmation email shortly.
                </p>
              )}

              {/* Totals */}
              <div style={{ borderTop: `1px solid ${borderColor}`, marginTop: '12px', paddingTop: '12px', fontSize: '0.875rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <span style={{ color: textSecondary }}>Subtotal</span>
                  <span style={{ color: textPrimary }}>${order.subtotal.toFixed(2)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                  <span style={{ color: textSecondary }}>Shipping</span>
                  <span style={{ color: order.shipping === 0 ? '#24a148' : textPrimary }}>
                    {order.shipping === 0 ? 'FREE' : `$${order.shipping.toFixed(2)}`}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <span style={{ color: textSecondary }}>Tax</span>
                  <span style={{ color: textPrimary }}>${order.tax.toFixed(2)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '12px', borderTop: `1px solid ${borderColor}` }}>
                  <span style={{ color: textPrimary, fontWeight: 600 }}>Total</span>
                  <span style={{ color: '#0f62fe', fontSize: '1.125rem', fontWeight: 600 }}>${order.total.toFixed(2)}</span>
                </div>
              </div>
            </Tile>

            {/* Shipping & Payment Info */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
              <Tile style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, padding: '20px' }}>
                <h4 style={{ color: textPrimary, fontSize: '0.875rem', fontWeight: 600, marginBottom: '8px' }}>Shipping Address</h4>
                {order.shippingAddress ? (
                  <p style={{ color: textSecondary, fontSize: '0.875rem', lineHeight: 1.6, margin: 0 }}>
                    {order.shippingAddress.firstName} {order.shippingAddress.lastName}<br />
                    {order.shippingAddress.address}
                    {order.shippingAddress.address2 && <><br />{order.shippingAddress.address2}</>}
                    <br />
                    {order.shippingAddress.city}{order.shippingAddress.state ? `, ${order.shippingAddress.state}` : ''} {order.shippingAddress.zipCode}
                    {order.shippingAddress.country && <><br />{order.shippingAddress.country}</>}
                  </p>
                ) : (
                  <p style={{ color: textSecondary, fontSize: '0.875rem', margin: 0 }}>Address on file</p>
                )}
              </Tile>
              <Tile style={{ backgroundColor: bgSecondary, border: `1px solid ${borderColor}`, padding: '20px' }}>
                <h4 style={{ color: textPrimary, fontSize: '0.875rem', fontWeight: 600, marginBottom: '8px' }}>Payment Method</h4>
                <p style={{ color: textSecondary, fontSize: '0.875rem', lineHeight: 1.6, margin: 0 }}>
                  {order.paymentMethod.includes('PayPal') ? 'PayPal' : 'Credit Card'}<br />
                  {order.paymentMethod}
                </p>
              </Tile>
            </div>

            {/* Navigation Buttons */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }} className="no-print">
              <Button
                kind="primary"
                size="lg"
                renderIcon={Package}
                onClick={() => navigate('/dashboard/orders')}
                style={{ width: '100%' }}
                data-testid="track-order"
              >
                Track Order
              </Button>
              <Button
                kind="secondary"
                size="lg"
                renderIcon={Home}
                onClick={() => navigate('/')}
                style={{ width: '100%' }}
                data-testid="continue-shopping"
              >
                Continue Shopping
              </Button>
            </div>
          </Column>
        </Grid>
      </div>

      {/* Email Receipt Modal */}
      <Modal
        open={isEmailModalOpen}
        onRequestClose={() => {
          setIsEmailModalOpen(false);
          setEmailError(null);
          setEmailSent(false);
        }}
        modalHeading="Email Receipt"
        primaryButtonText={emailSent ? "Sent!" : "Send Receipt"}
        secondaryButtonText="Cancel"
        onRequestSubmit={handleEmailReceipt}
        primaryButtonDisabled={emailSending || emailSent || !emailAddress}
        size="sm"
      >
        <div style={{ padding: '16px 0' }}>
          {emailSent ? (
            <InlineNotification
              kind="success"
              title="Receipt Sent!"
              subtitle={`Receipt has been sent to ${emailAddress}`}
              iconDescription="Success"
            />
          ) : (
            <>
              <p style={{ color: textSecondary, marginBottom: '16px', fontSize: '0.875rem' }}>
                Enter the email address where you'd like to receive your receipt.
              </p>
              <TextInput
                id="receipt-email"
                labelText="Email Address"
                placeholder="your@email.com"
                value={emailAddress}
                onChange={(e) => {
                  setEmailAddress(e.target.value);
                  setEmailError(null);
                }}
                invalid={!!emailError}
                invalidText={emailError}
                disabled={emailSending}
                style={{ marginBottom: '16px' }}
              />
              {emailSending && (
                <InlineLoading description="Sending receipt..." />
              )}
            </>
          )}
        </div>
      </Modal>

      {/* Print Styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .print-content, .print-content * {
            visibility: visible;
          }
          .print-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          .no-print {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default PaymentConfirmationPage;
